/**
 * Mappings → Autotask settings, Logging to Autotask (ADR 0172; moved here from
 * Settings by ADR 0198): whether each log adds the activity-log note to its
 * ticket, and whether the Log form's "Close ticket after logging" starts
 * ticked. Beside the note's type and who can see it, under Ticket notes above,
 * which the note needs while it's on.
 *
 * Its own panel, from the settings alone: it works before Autotask's first
 * sync, when the Autotask IDs above have nothing to choose from yet.
 *
 * Each change saves as it's made (ADR 0082), as on Settings.
 */
import { useQuery } from "@tanstack/react-query";

import { retryUnlessRefused } from "../../api/client";
import type { AppSettings } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Panel } from "../../components/ui/Panel";
import { CheckboxField } from "../settings/fields";
import { useSettingsSection } from "../settings/useSettingsSection";

type LoggingKey = "create_ticket_note" | "close_ticket_default";

function LoggingToAutotask({ settings }: { settings: AppSettings }) {
  const { saved, shown, change, statusOf, errors } = useSettingsSection<LoggingKey>(settings);
  const unchosen = [
    saved.autotask_note_type_id === null ? "Note type" : null,
    saved.autotask_note_publish_id === null ? "Who can see the activity note" : null,
  ].filter((label) => label !== null);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-2">
        <CheckboxField
          label="Add the activity-log note to the ticket"
          hint={
            <>
              Each log adds a note to its ticket listing the block&apos;s activities. A new ticket&apos;s
              description lists them too, whether this is on or off. While this is on,{" "}
              <span className="font-medium">Note type</span> and{" "}
              <span className="font-medium">Who can see the activity note</span> must be chosen above, under
              Ticket notes; while it&apos;s off, neither is needed. On to start.
            </>
          }
          checked={shown("create_ticket_note")}
          status={statusOf("create_ticket_note")}
          error={errors.create_ticket_note}
          onSave={(on) => {
            change({ create_ticket_note: on });
          }}
        />
        {saved.create_ticket_note && unchosen.length > 0 ? (
          <p className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg">
            {`Not chosen yet: ${unchosen.join(" and ")}. A log can't be written until ${
              unchosen.length === 1 ? "it is" : "they are"
            }.`}
          </p>
        ) : null}
      </div>
      <CheckboxField
        label="Close ticket after logging starts ticked"
        hint="Whether the Log form's Close ticket after logging box starts ticked. A tech can change it on each log. On to start."
        checked={shown("close_ticket_default")}
        status={statusOf("close_ticket_default")}
        error={errors.close_ticket_default}
        onSave={(on) => {
          change({ close_ticket_default: on });
        }}
      />
    </div>
  );
}

export function LoggingSettings() {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });

  let body;
  // A failed reload keeps the form it already has.
  if (settings.error && !settings.data) {
    body = (
      <p role="alert" className="text-muted">
        The settings could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!settings.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else {
    body = <LoggingToAutotask settings={settings.data} />;
  }

  return (
    <Panel aria-labelledby="logging-heading">
      <h2 id="logging-heading" className="text-lg font-semibold tracking-tight">
        Logging to Autotask
      </h2>
      <div className="mt-3">{body}</div>
    </Panel>
  );
}
